import type {GraphQLTaggedNode, OperationType, PreloadableConcreteRequest, Variables} from 'relay-runtime'
import {
  INTERNAL_ONLY_ATTRIBUTES,
  INVALID,
  type CacheHitSource,
  type CacheKey,
  type Invalid,
  type QueryParameters,
} from './types'
import {isStaff} from '@github-ui/stats'
import {reportTraceData} from '@github-ui/internal-api-insights'
import type {CircuitBreaker} from './circuit-breaker'

export const DEFAULT_RELAY_CACHE_TTL = 2 * 7 * 24 * 60 * 60 * 1000 // 2 weeks in milliseconds

/**
 * Creates a stable key from variables by sorting keys alphabetically.
 * Merges in default values for any missing variable keys.
 */
export function createVariablesKey(variables: Variables, defaults: Record<string, unknown> | undefined): string {
  const merged = mergeVariablesWithDefaults(variables, defaults)
  return JSON.stringify(merged, Object.keys(merged).sort())
}

/**
 * Merges variables with default values from query metadata.
 * For any variable key present in defaultValues but not in variables, the default value is used.
 */
function mergeVariablesWithDefaults(
  variables: Variables,
  defaultValues: Record<string, unknown> | undefined,
): Variables {
  return {...defaultValues, ...variables}
}

export function getDefaultValues<TQuery extends OperationType>(
  query: GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>,
): Record<string, unknown> | undefined {
  const defaultValues: Record<string, unknown> = {}
  if ('operation' in query) {
    const argumentDefinitions = query.operation.argumentDefinitions
    for (const argDef of argumentDefinitions) {
      if (argDef.defaultValue !== undefined && argDef.defaultValue !== null) {
        defaultValues[argDef.name] = argDef.defaultValue
      }
    }
  }
  return Object.keys(defaultValues).length > 0 ? defaultValues : undefined
}

export function getUpdateType<T>(
  cachedResult: Invalid | Awaited<T> | undefined,
  fetchResult: T,
  preHeated: boolean | undefined,
  skipCache: boolean | undefined,
) {
  if (skipCache) {
    return 'force_refresh'
  }
  if (!cachedResult) {
    return preHeated ? 'pre_heated' : 'new'
  }
  let cachedResultString: string | null = null
  let queryResultString: string | null = null
  // Only cache successful results
  if (typeof fetchResult === 'object' && fetchResult !== null && 'data' in fetchResult) {
    queryResultString = JSON.stringify((fetchResult as {[key: string]: unknown}).data)
  }
  if (!!cachedResult && typeof cachedResult === 'object' && cachedResult !== null && 'data' in cachedResult) {
    cachedResultString = JSON.stringify((cachedResult as {[key: string]: unknown}).data)
  }

  return cachedResultString !== queryResultString ? 'changed' : 'unchanged'
}

export function cleanVariables(
  variables: Record<string, unknown>,
  defaultValues?: Record<string, unknown>,
  excludeKeys?: string[],
): Record<string, unknown> {
  const variablesWithDefaults = {
    ...defaultValues,
    ...Object.fromEntries(Object.entries(variables ?? {}).filter(([, v]) => v !== undefined)),
  }
  const sortedVariableKeys = Object.keys(variablesWithDefaults).sort()
  const cleanedVariables: Record<string, unknown> = {}
  for (const key of sortedVariableKeys) {
    if (Array.isArray(excludeKeys) && excludeKeys.includes(key)) {
      continue
    }
    const value = variablesWithDefaults[key]
    if (value !== undefined && value !== null) {
      cleanedVariables[key] = value
    }
  }
  return cleanedVariables
}

export function generateCacheKey(
  prefix: string,
  queryParameters: QueryParameters,
  defaultValues?: Record<string, unknown>,
  excludeFromCacheKey?: string[],
): CacheKey {
  const cleanedVariables = cleanVariables(queryParameters.variables, defaultValues, excludeFromCacheKey)
  return `${prefix}-${queryParameters.queryName}#${JSON.stringify(cleanedVariables)}`
}

export function removeInternalOnlyAttributes(attributes: {[key: string]: unknown} | undefined) {
  if (!attributes) return undefined

  const cleanedAttributes: {[key: string]: unknown} = {}
  for (const [key, value] of Object.entries(attributes)) {
    if (
      !Object.values(INTERNAL_ONLY_ATTRIBUTES).includes(
        key as (typeof INTERNAL_ONLY_ATTRIBUTES)[keyof typeof INTERNAL_ONLY_ATTRIBUTES],
      )
    ) {
      cleanedAttributes[key] = value
    }
  }
  return cleanedAttributes
}

export function constructCacheEntry<T>(
  queryParameters: QueryParameters,
  ttl: number,
  defaultValues?: Record<string, unknown>,
  _keysToExclude?: string[],
  preheatSource?: string,
  fetchResult?: T,
) {
  return {
    queryHash: queryParameters.queryId,
    variables: cleanVariables(queryParameters.variables, defaultValues),
    state: {data: fetchResult, dataUpdatedAt: Date.now(), ttl, preheatSource},
  }
}

export function shouldClearCache<T>(fetchResult: T, cachedResult: T | undefined | Invalid): boolean {
  if (!cachedResult) return false
  if (cachedResult === INVALID) return true
  if (!fetchResult) return false

  return hasErrors(fetchResult)
}

export function hasErrors<T>(result: T): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    'errors' in result &&
    Array.isArray(result.errors) &&
    result.errors.length > 0
  )
}

export function dispatchStaleCacheWarning(errors: unknown[]) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent('stale-cache-warning', {
      detail: {
        errors,
      },
    }),
  )
}

/**
 * Dispatches a custom event for staff bar to indicate a cache hit was used for rendering.
 * This is only dispatched when the cached data is actually displayed to the user,
 * not for preheat scenarios. Only dispatched for staff users.
 *
 * @param source - The source of the cache hit (indexed-db, hot-cache, or service-worker)
 */
export function dispatchCacheHitEvent(source: CacheHitSource = 'indexed-db') {
  if (typeof window === 'undefined') return
  if (!isStaff()) return

  window.dispatchEvent(new CustomEvent('relay-cache-hit', {detail: {source}}))
}

export function shouldWriteToCache<T>(fetchResult: T): boolean {
  if (!fetchResult) return false

  if (typeof fetchResult === 'object') {
    if ('errors' in fetchResult && Array.isArray(fetchResult.errors) && fetchResult.errors.length > 0) {
      return false
    }
  }
  return true
}

export function reportQueryForInsightsApi(queryParameters: QueryParameters) {
  reportTraceData({
    __trace: {
      query_name: queryParameters.queryName,
      query_id: queryParameters.queryId,
      query_variables: queryParameters.variables,
      duration_ms: 1,
      cache_result: 'hit',
    },
  })
}

export function shouldSkipNetwork(
  skipNetwork: boolean,
  preheatSource: string | undefined,
  hasCachedResult: boolean,
  circuitBreaker: CircuitBreaker,
): boolean {
  if (skipNetwork) return true
  if (preheatSource) {
    if (circuitBreaker.getStatus() === 'OPEN') {
      return true
    }
    if (hasCachedResult && preheatSource !== 'force_refresh') {
      // If preHeated is set and we have a cached result, no need to hit the network
      return true
    }
  }
  return false
}
